// Compare lens operational pattern (FE-063, FRONTEND_TASKS.md §5).
// Lines up two explicitly selected evidence versions on shared anchors.
// Labels all differences explicitly with non-colour indicators and text badges.
// Supports accessible linear reading and quiet reduced-motion operation.

import { useId, useMemo, useState } from 'react';
import { neutralPointer } from '../features/discovery/collectorLabel.ts';
import {
  ArrowLeftRight,
  Columns2,
  ListFilter,
  RotateCcw,
  Rows3,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import { Button } from './Button.tsx';
import { SelectField } from './SelectField.tsx';
import { buildEvidenceComparison, type EvidenceCompareItem } from '../features/findings/compare.ts';
import styles from './CompareLens.module.css';

export interface CompareLensProps {
  evidenceList: readonly EvidenceCompareItem[];
  selectedBaselineId?: string | null;
  selectedTargetId?: string | null;
  onSelectBaseline?: (id: string | null) => void;
  onSelectTarget?: (id: string | null) => void;
  initialViewMode?: 'linear' | 'split';
  onViewModeChange?: (mode: 'linear' | 'split') => void;
  onClose?: () => void;
}

export function CompareLens({
  evidenceList,
  selectedBaselineId,
  selectedTargetId,
  onSelectBaseline,
  onSelectTarget,
  initialViewMode = 'linear',
  onViewModeChange,
  onClose,
}: CompareLensProps) {
  const headingId = useId();

  // Internal state when uncontrolled, synced with props when controlled
  const [internalBaselineId, setInternalBaselineId] = useState<string | null>(
    selectedBaselineId ?? null,
  );
  const [internalTargetId, setInternalTargetId] = useState<string | null>(selectedTargetId ?? null);

  const baselineId = selectedBaselineId !== undefined ? selectedBaselineId : internalBaselineId;
  const targetId = selectedTargetId !== undefined ? selectedTargetId : internalTargetId;

  // View presentation mode: 'linear' (stacked sequential) or 'split' (side-by-side aligned)
  const [viewMode, setViewMode] = useState<'linear' | 'split'>(initialViewMode);

  // Filter mode: 'all' | 'diffs' | 'unchanged'
  const [filterMode, setFilterMode] = useState<'all' | 'diffs' | 'unchanged'>('all');

  const handleBaselineChange = (idStr: string) => {
    const val = idStr === 'NONE' || !idStr ? null : idStr;
    setInternalBaselineId(val);
    onSelectBaseline?.(val);
  };

  const handleTargetChange = (idStr: string) => {
    const val = idStr === 'NONE' || !idStr ? null : idStr;
    setInternalTargetId(val);
    onSelectTarget?.(val);
  };

  const handleSwap = () => {
    const oldBase = baselineId;
    const oldTarget = targetId;
    setInternalBaselineId(oldTarget);
    setInternalTargetId(oldBase);
    onSelectBaseline?.(oldTarget);
    onSelectTarget?.(oldBase);
  };

  const handleClear = () => {
    setInternalBaselineId(null);
    setInternalTargetId(null);
    onSelectBaseline?.(null);
    onSelectTarget?.(null);
  };

  const handleViewModeToggle = (mode: 'linear' | 'split') => {
    setViewMode(mode);
    onViewModeChange?.(mode);
  };

  // Find selected items
  const baselineItem = useMemo(
    () => evidenceList.find((e) => e.id === baselineId) ?? null,
    [evidenceList, baselineId],
  );

  const targetItem = useMemo(
    () => evidenceList.find((e) => e.id === targetId) ?? null,
    [evidenceList, targetId],
  );

  // Build comparison result
  const comparison = useMemo(
    () => buildEvidenceComparison(baselineItem, targetItem),
    [baselineItem, targetItem],
  );

  // Dropdown options
  const selectOptions = useMemo(() => {
    const options: Array<readonly [string, string]> = [['NONE', 'Select an evidence record…']];
    for (const item of evidenceList) {
      options.push([
        item.id,
        `${item.id} · ${neutralPointer(item.sourcePointer)} (${item.kind})`,
      ] as const);
    }
    return options;
  }, [evidenceList]);

  // Filter anchors based on filterMode
  const filteredAnchors = useMemo(() => {
    if (filterMode === 'diffs') {
      return comparison.anchors.filter((a) => a.status !== 'UNCHANGED');
    }
    if (filterMode === 'unchanged') {
      return comparison.anchors.filter((a) => a.status === 'UNCHANGED');
    }
    return comparison.anchors;
  }, [comparison.anchors, filterMode]);

  const bothSelected = Boolean(baselineItem && targetItem);

  return (
    <section className={styles.compareLens} aria-labelledby={headingId} data-testid="compare-lens">
      {/* Lens Header */}
      <header className={styles.lensHeader}>
        <div className={styles.titleRow}>
          <div className={styles.lensTitleGroup}>
            <p className={styles.lensEyebrow}>Dual-version inspection</p>
            <h2 id={headingId} className={styles.lensHeading}>
              <ArrowLeftRight size={20} aria-hidden="true" />
              <span>Compare lens</span>
            </h2>
          </div>

          <div className={styles.controlsGroup}>
            {onClose && (
              <Button
                variant="secondary"
                size="small"
                onPress={onClose}
                aria-label="Exit Compare lens"
              >
                <span>Exit Compare lens</span>
              </Button>
            )}
          </div>
        </div>

        <p className={styles.lensDescription}>
          Line up two evidence versions along shared structural anchors. All changes and drift
          states are explicitly labelled with text and non-colour glyph markers. The comparison can
          be read sequentially in linear order or side-by-side.
        </p>
      </header>

      {/* Explicit Selection Toolbar */}
      <div className={styles.selectionToolbar} role="group" aria-label="Version selection controls">
        <div className={styles.selectorField}>
          <SelectField
            label="Baseline version (A)"
            value={baselineId ?? 'NONE'}
            onChange={handleBaselineChange}
            options={selectOptions}
          />
        </div>

        <div className={styles.swapButtonWrapper}>
          <Button
            variant="secondary"
            size="small"
            onPress={handleSwap}
            isDisabled={!baselineId && !targetId}
            aria-label="Swap baseline and comparison versions"
          >
            <ArrowLeftRight size={14} aria-hidden="true" />
            <span>Swap</span>
          </Button>
        </div>

        <div className={styles.selectorField}>
          <SelectField
            label="Comparison version (B)"
            value={targetId ?? 'NONE'}
            onChange={handleTargetChange}
            options={selectOptions}
          />
        </div>

        <div className={styles.controlsGroup}>
          <Button
            variant="tertiary"
            size="small"
            onPress={handleClear}
            isDisabled={!baselineId && !targetId}
            aria-label="Clear version selections"
          >
            <RotateCcw size={14} aria-hidden="true" />
            <span>Clear</span>
          </Button>
        </div>
      </div>

      {/* Guidance when both versions are not explicitly selected yet */}
      {!bothSelected ? (
        <div
          className={styles.unselectedPrompt}
          role="status"
          aria-live="polite"
          data-testid="unselected-prompt"
        >
          <div className={styles.unselectedTitle}>
            {!baselineItem && !targetItem
              ? 'Select two evidence versions to begin'
              : !baselineItem
                ? 'Select a baseline version (A) to compare against'
                : 'Select a comparison version (B) to line up on shared anchors'}
          </div>
          <p className={styles.unselectedText}>
            Both versions must be explicitly selected above. Once chosen, the lens will align their
            identity, classification, collector provenance, cryptographic hash, and observation
            payloads.
          </p>
        </div>
      ) : (
        <>
          {/* Summary KPI Chips Bar */}
          <div
            className={styles.summaryBar}
            role="region"
            aria-label="Comparison summary statistics"
          >
            <div className={styles.summaryChip} title="Total shared anchors evaluated">
              <span className={styles.chipCount}>{comparison.summary.totalAnchors}</span>
              <span className={styles.chipLabel}>Shared anchors</span>
            </div>

            <div
              className={styles.summaryChipMatched}
              title="Anchors with identical values across both versions"
            >
              <ShieldCheck size={14} aria-hidden="true" />
              <span className={styles.chipCount}>{comparison.summary.unchangedCount}</span>
              <span className={styles.chipLabel}>[MATCH] Unchanged</span>
            </div>

            {comparison.summary.modifiedCount > 0 && (
              <div
                className={styles.summaryChipModified}
                title="Anchors with modified values between versions"
              >
                <span className={styles.chipCount}>{comparison.summary.modifiedCount}</span>
                <span className={styles.chipLabel}>[MODIFIED] Changed</span>
              </div>
            )}

            {comparison.summary.addedCount > 0 && (
              <div
                className={styles.summaryChip}
                title="Anchors present in comparison version only"
              >
                <span className={styles.chipCount}>{comparison.summary.addedCount}</span>
                <span className={styles.chipLabel}>[ADDED] New</span>
              </div>
            )}

            {comparison.summary.removedCount > 0 && (
              <div className={styles.summaryChip} title="Anchors present in baseline only">
                <span className={styles.chipCount}>{comparison.summary.removedCount}</span>
                <span className={styles.chipLabel}>[REMOVED] Omitted</span>
              </div>
            )}

            {comparison.summary.driftCount > 0 && (
              <div
                className={styles.summaryChipDrift}
                title="Integrity or collector drift detected"
              >
                <ShieldAlert size={14} aria-hidden="true" />
                <span className={styles.chipCount}>{comparison.summary.driftCount}</span>
                <span className={styles.chipLabel}>[DRIFT] Attention required</span>
              </div>
            )}
          </div>

          {/* View & Filter Options Bar */}
          <div
            className={styles.viewOptionsBar}
            role="toolbar"
            aria-label="Comparison presentation and filtering controls"
          >
            {/* View Mode Toggle: Linear vs Split */}
            <div
              className={styles.viewModeToggle}
              role="radiogroup"
              aria-label="Reading layout mode"
            >
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'linear'}
                data-active={viewMode === 'linear' ? 'true' : 'false'}
                className={styles.viewModeBtn}
                onClick={() => handleViewModeToggle('linear')}
              >
                <Rows3 size={14} aria-hidden="true" />
                <span>Linear reading</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'split'}
                data-active={viewMode === 'split' ? 'true' : 'false'}
                className={styles.viewModeBtn}
                onClick={() => handleViewModeToggle('split')}
              >
                <Columns2 size={14} aria-hidden="true" />
                <span>Split aligned</span>
              </button>
            </div>

            {/* Filter Toggle Group */}
            <div
              className={styles.filterToggleGroup}
              role="radiogroup"
              aria-label="Filter anchors by difference status"
            >
              <Button
                variant={filterMode === 'all' ? 'primary' : 'secondary'}
                size="small"
                onPress={() => setFilterMode('all')}
                aria-label="Show all anchors"
              >
                <span>All ({comparison.summary.totalAnchors})</span>
              </Button>
              <Button
                variant={filterMode === 'diffs' ? 'primary' : 'secondary'}
                size="small"
                onPress={() => setFilterMode('diffs')}
                aria-label="Show differences only"
              >
                <ListFilter size={14} aria-hidden="true" />
                <span>
                  Differences only (
                  {comparison.summary.totalAnchors - comparison.summary.unchangedCount})
                </span>
              </Button>
              <Button
                variant={filterMode === 'unchanged' ? 'primary' : 'secondary'}
                size="small"
                onPress={() => setFilterMode('unchanged')}
                aria-label="Show unchanged anchors only"
              >
                <span>Unchanged only ({comparison.summary.unchangedCount})</span>
              </Button>
            </div>
          </div>

          {/* Anchors Deck: Either Split Aligned Table or Linear Stacked Cards */}
          <div className={styles.anchorsContainer}>
            {viewMode === 'split' ? (
              <table
                className={styles.sideBySideTable}
                role="table"
                aria-label="Aligned evidence comparison"
              >
                <thead>
                  <tr>
                    <th className={`${styles.thHeader} ${styles.thAnchorCol}`} scope="col">
                      Shared Anchor &amp; Labelled Difference
                    </th>
                    <th className={`${styles.thHeader} ${styles.thVersionCol}`} scope="col">
                      <div className={styles.versionTitleBadge}>
                        <span className={styles.versionRole}>Baseline (A)</span>
                        <code className={styles.versionId}>{baselineItem?.id}</code>
                      </div>
                    </th>
                    <th className={`${styles.thHeader} ${styles.thVersionCol}`} scope="col">
                      <div className={styles.versionTitleBadge}>
                        <span className={styles.versionRole}>Comparison (B)</span>
                        <code className={styles.versionId}>{targetItem?.id}</code>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnchors.map((anchor) => (
                    <tr key={anchor.id} className={styles.anchorRow} data-status={anchor.status}>
                      <td className={styles.tdAnchor}>
                        <div className={styles.anchorMeta}>
                          <div className={styles.anchorHeaderRow}>
                            <h3 className={styles.anchorLabel}>{anchor.label}</h3>
                            <span
                              className={styles.statusPill}
                              data-status={anchor.status}
                              aria-label={`Status: ${anchor.statusLabel}`}
                            >
                              <span aria-hidden="true">{anchor.glyph}</span>
                              <span>{anchor.statusLabel}</span>
                            </span>
                          </div>
                          <span className={styles.anchorCategory}>{anchor.category}</span>
                          <p className={styles.diffExplanation}>{anchor.diffExplanation}</p>
                        </div>
                      </td>
                      <td className={styles.tdVersion}>
                        {anchor.baselineValue !== null ? (
                          anchor.isMonospace ? (
                            <code className={styles.cellValueMono}>{anchor.baselineValue}</code>
                          ) : (
                            <span className={styles.cellValue}>{anchor.baselineValue}</span>
                          )
                        ) : (
                          <span className={styles.omittedPlaceholder}>[Omitted in baseline]</span>
                        )}
                      </td>
                      <td className={styles.tdVersion}>
                        {anchor.targetValue !== null ? (
                          anchor.isMonospace ? (
                            <code className={styles.cellValueMono}>{anchor.targetValue}</code>
                          ) : (
                            <span className={styles.cellValue}>{anchor.targetValue}</span>
                          )
                        ) : (
                          <span className={styles.omittedPlaceholder}>[Omitted in target]</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              /* Linear Accessible Reading Mode (Sequential DOM & Screen-Reader Optimized) */
              <div className={styles.linearList} role="feed" aria-label="Sequential shared anchors">
                {filteredAnchors.map((anchor) => (
                  <article
                    key={anchor.id}
                    className={styles.linearCard}
                    data-status={anchor.status}
                    aria-label={`Anchor: ${anchor.label}, Status: ${anchor.statusLabel}`}
                  >
                    <div className={styles.linearCardHeader}>
                      <div className={styles.anchorMeta}>
                        <h3 className={styles.anchorLabel}>{anchor.label}</h3>
                        <span className={styles.anchorCategory}>{anchor.category}</span>
                      </div>

                      <span
                        className={styles.statusPill}
                        data-status={anchor.status}
                        aria-label={`Status: ${anchor.statusLabel}`}
                      >
                        <span aria-hidden="true">{anchor.glyph}</span>
                        <span>{anchor.statusLabel}</span>
                      </span>
                    </div>

                    <p className={styles.diffExplanation}>{anchor.diffExplanation}</p>

                    <div className={styles.linearComparisonBody}>
                      <div className={styles.linearValuePane}>
                        <div className={styles.linearPaneHeading}>
                          <span>Baseline (A)</span>
                          <code className={styles.versionId}>{baselineItem?.id}</code>
                        </div>
                        {anchor.baselineValue !== null ? (
                          anchor.isMonospace ? (
                            <code className={styles.cellValueMono}>{anchor.baselineValue}</code>
                          ) : (
                            <p className={styles.linearPaneValue}>{anchor.baselineValue}</p>
                          )
                        ) : (
                          <span className={styles.omittedPlaceholder}>[Omitted in baseline]</span>
                        )}
                      </div>

                      <div className={styles.linearValuePane}>
                        <div className={styles.linearPaneHeading}>
                          <span>Comparison (B)</span>
                          <code className={styles.versionId}>{targetItem?.id}</code>
                        </div>
                        {anchor.targetValue !== null ? (
                          anchor.isMonospace ? (
                            <code className={styles.cellValueMono}>{anchor.targetValue}</code>
                          ) : (
                            <p className={styles.linearPaneValue}>{anchor.targetValue}</p>
                          )
                        ) : (
                          <span className={styles.omittedPlaceholder}>[Omitted in target]</span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
